import { Platform } from '@angular/cdk/platform';
import { Directive, OnInit, ViewChild, ElementRef, OnDestroy, inject, signal } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { translate } from '@jsverse/transloco';
import { toBlob } from 'html-to-image';

import { takeUntil } from 'rxjs';

import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { Comment, CommentTarget } from 'app/providers/bridge-api/dashboard.service';
import { Constants } from '../../../../constants/constants';
import { WidgetDataSource } from '../../../../models';
import { AuthService } from '../../../../providers/bridge-api/auth.service';
import { WidgetDefault } from '../../../../providers/bridge-api/dashboard.service';
import { MatchmakerHandler } from '../../../../providers/semantria-api/spote.service';
import { StorageAccountService } from '../../../../providers/storage-api/blender-account.service';
import { DocumentService, SearchOptions } from '../../../../providers/storage-api/document.service';
import { InteractiveSmartSummaryService } from '../../../../providers/storage-api/interactive-smart-summary.service';
import { ProjectService } from '../../../../providers/storage-api/project.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';
import { SystemService } from '../../../../services/system.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { WidgetLimiterService } from '../../../../services/widget-limiter.service';
import { Utils } from '../../../../utils/utils';
import { FilterService, WidgetQueryOptions } from '../../../collection/services/filter.service';
import { DashboardViewComponent } from '../../../dashboard/pages/dashboard-view/dashboard-view.component';
import {
  DownloadAsDialogComponent,
  DownloadAsDialogOptions,
  DownloadAsDialogResult,
} from './../../../collection/components/download-as-dialog/download-as-dialog.component';
import {
  ExpansionPanelType,
  WidgetCommentsHandlerService,
} from '../../services/widget-comments-handler.service';
import { AbstractWidgetComponent } from './abstract-widget.component';
import { WidgetComponent } from './widget.component';

@Directive()
export abstract class AbstractTableWidgetComponent<T extends WidgetDefault>
  extends AbstractWidgetComponent<T>
  implements OnInit, OnDestroy
{
  @ViewChild('menuTriggerElement') menuTriggerElement: MatMenuTrigger;
  @ViewChild('menuTriggerElement', { read: ElementRef })
  menuTriggerNativeElement: ElementRef;
  @ViewChild('tableWrapper') tableWrapper: ElementRef;

  noData = false;
  showDownloadCsvDialog = false;
  pointHasDocuments: boolean = true;

  itemClicked: {
    row: any;
    column: string;
    item: string;
    itemDisplayName?: string;
    axis?: 'row' | 'column';
    axisItems?: string[];
    value?: number;
    documentCount?: number;
  };
  focusAreaItem: {};
  commentsByPointId = signal(new Map<string, Comment[]>());
  selectedCommentPointId: string;
  canCreateSmartSummary: boolean = true;
  downloadSmartSummaryRequestToolTip: string;
  platform = inject(Platform);

  insightPointSelectionState = {
    active: false,
    pointCount: 0,
    maxPoints: 0,
    pointSelectionRule: undefined,
    isApplyComparison: false,
  };

  protected constructor(
    protected filterService: FilterService,
    protected dialogService: DialogService,
    protected documentService: DocumentService,
    protected projectService: ProjectService,
    protected alertService: AlertService,
    protected widgetLimiterService: WidgetLimiterService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected dashboardViewComponent: DashboardViewComponent,
    protected widgetComponent: WidgetComponent,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();

    const initialComments = new Map<string, Comment[]>();
    this.payload.comments
      ?.filter((c) => c.payload.widgetDataSourceId === this.widget.dataSourceId)
      .forEach((c) => {
        if (c.target?.pointId) {
          if (!initialComments.has(c.target.pointId)) {
            initialComments.set(c.target.pointId, []);
          }
          initialComments.get(c.target.pointId).push(c);
        }
      });
    this.commentsByPointId.set(initialComments);

    this.commentsHandlerService.hideExpansionPanel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.itemClicked = null));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  async refresh(_reset = false, initialLoad = false, refreshedDatasource?: WidgetDataSource) {
    this.widgetDataSource = refreshedDatasource ?? this.widgetDataSource;
    this.dataSourceNotFound = refreshedDatasource ? false : this.dataSourceNotFound;
    this.widgetInfo = undefined;
    this.isLoading = true;
    if (this.dataSourceNotFound || this.hasInvalidComparison) {
      this.loadingStarted.emit(initialLoad);
      this.loadingFinished.emit(initialLoad);
      this.isLoading = false;
      return;
    }

    if (initialLoad) {
      this.interactiveSmartSummaryService
        .getSmartSummaries(this.dashboard.project_id, this.dashboard.id, this.widget.id)
        .then((res) => {
          this.storedSmartSummaries = res;
          this.isSmartSummaryGenerating = this.storedSmartSummaries.some(
            (sum) => sum.status === 'RUNNING'
          );
          if (this.isSmartSummaryEnabled) {
            this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
            this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
          }
        });
    }
  }

  protected calculateMenuPosition($event) {
    const targetCell = $event.target.closest('td,th');

    let scrollLeft = targetCell.offsetParent.parentElement.scrollLeft;
    let scrollTop = targetCell.offsetParent.parentElement.scrollTop;

    if (targetCell.offsetParent.parentElement.classList.contains('ps-content')) {
      scrollLeft = parseInt(
        targetCell.offsetParent.parentElement.parentElement.querySelector('.ps__rail-x').style.left,
        10
      );
      scrollTop = parseInt(
        targetCell.offsetParent.parentElement.parentElement.querySelector('.ps__rail-y').style.top,
        10
      );
    }

    const left =
      $event.offsetX + targetCell.offsetLeft + targetCell.offsetParent.offsetLeft - scrollLeft;
    const top =
      $event.offsetY + targetCell.offsetTop + targetCell.offsetParent.offsetTop - scrollTop;

    return { left, top };
  }

  openCellMenu($event, row, column, extraData?) {
    if (this.dashboard.locked || this.widgetComponent.isExpansionPanelShowing) {
      if (
        this.insightPointSelectionState.active &&
        this.insightPointSelectionState.pointCount < this.insightPointSelectionState.maxPoints
      ) {
        this.onInsightPointSelection(row, column, extraData);
      }

      return;
    }

    setTimeout(() => {
      const { left, top } = this.calculateMenuPosition($event);
      this.menuTriggerNativeElement.nativeElement.setAttribute(
        'style',
        `left: ${left}px; top: ${top}px`
      );

      this.initializePointData(row, column, extraData);

      if (this.isSmartSummaryEnabled) {
        this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
        this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
      }
      this.downloadSmartSummaryRequestToolTip = this.buildSmartSummaryDownloadTooltip();
      this.menuTriggerElement.openMenu();
      this.cdr.markForCheck();
    });
  }

  private buildSmartSummaryDownloadTooltip() {
    if (this.payload.transpose) {
      return translate('widgets.smart-summary.transpose-tooltip');
    }

    if (!this.canCreateSmartSummary) {
      return translate('widgets.smart-summary.unavailable-for-points-with-less-than-documents', {
        count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
      });
    }
    return '';
  }

  private initializePointData(row, column, extraData) {
    this.menuTriggerElement.menuData = this.getCellMenuData(row, column, extraData);
    this.itemClicked = {
      row,
      column,
      value: row && column && row[column],
      documentCount: row && column && row[column],
      ...this.menuTriggerElement.menuData,
    };

    if (
      extraData !== 'row' &&
      extraData !== 'column' &&
      (isNaN(this.itemClicked.value) || typeof this.itemClicked.value === 'string')
    ) {
      this.itemClicked.value = 0;
      this.itemClicked.documentCount = row?.documentCount ?? 0;
    }
    this.pointHasDocuments = this.hasPointDocuments(this.itemClicked);
    this.canCreateSmartSummary = this.pointHasRequiredDocCountForSmartSummaries(this.itemClicked);
    this.pointComments = this.getCommentsForPoint(this.menuTriggerElement.menuData);
    this.pointSummaries = this.getSmartSummariesForPoint(this.menuTriggerElement.menuData.item);
  }

  public async getDocuments(widgetQueryOptions, documentViewOptions = {}) {
    const query = this.filterService.getQueryForDocuments(widgetQueryOptions);

    const searchOptions: SearchOptions = {
      timezone: this.timezoneService.timezone,
    };

    const page = await this.documentService.searchForDocuments(
      this.widgetDataSource,
      this.dashboard.project_id,
      this.widgetDataSource.collectionName,
      query,
      20,
      searchOptions
    );

    const documentId = page.count ? page.items[0].id : null;
    if (documentId == null) {
      this.alertService.showErrorToast(translate('widgets.error-receiving-documents'));
      return;
    }

    const project = await this.projectService.getProjectById(this.dashboard.project_id, false);

    this.documentViewModalOptions = {
      collectionName: page.items[0].collectionName,
      page,
      pageIndex: 0,
      documentIndex: 0,
      limit: 20,
      documentId,
      projectId: this.dashboard.project_id,
      sentimentScale: this.payload.sentimentScale,
      sentimentDisplay: this.dashboard.payload.sentimentDisplay,
      dashboardId: this.dashboard.id,
      isDashboardLocked: this.dashboard.locked,
      sentimentRanges: {
        feature: project.polarities.feature[this.payload.sentimentScale],
        document: project.polarities.document[this.payload.sentimentScale],
      },
      widgetDataSource: this.widgetDataSource,
      ...documentViewOptions,
    };
    this.showDocumentViewModal = true;
  }

  onDocumentViewModalClosed(result?) {
    this.showDocumentViewModal = false;
    if (result) {
      this.dashboardViewComponent.reload();
    }
  }

  public async hideItem(item, selectedItems = this.widget.payload.selectedItems) {
    if (this.gethasCustomSet()) {
      if (selectedItems) {
        // hiding the item means removing it from the custom set according to DASH-1466
        const idx = selectedItems.indexOf(item.name);
        if (idx >= 0) {
          selectedItems.splice(idx, 1);
        }
        this.dashboardStore.markDirty();

        return this.refresh();
      }
    }

    if (!this.widget.payload.excludedValues) {
      (this.widget.payload as any).excludedValues = [];
    }

    this.widget.payload.excludedValues.push(item);
    this.widget.payload.excludedValues.sort((a, b) =>
      (a.displayName || a.name || a).localeCompare(b.displayName || b.name || b)
    );
    this.dashboardStore.markDirty();

    if (!this.gethasCustomSet()) {
      return this.refresh();
    }
  }

  async exportAsImage(): Promise<any> {
    const currentScrollLeft = this.tableWrapper.nativeElement.scrollLeft;
    const currentScrollTop = this.tableWrapper.nativeElement.scrollTop;
    this.tableWrapper.nativeElement.scroll(0, 0);

    const table =
      this.tableWrapper.nativeElement.getElementsByTagName('table').item(0) ||
      this.tableWrapper.nativeElement;

    return toBlob(table, {
      filter: (domNode) =>
        domNode.nodeType === Node.TEXT_NODE ||
        (domNode.nodeType !== Node.COMMENT_NODE &&
          window.getComputedStyle(domNode).display !== 'none' &&
          window.getComputedStyle(domNode).visibility !== 'hidden'),
      // This i just a temporary fix for more visit: https://github.com/bubkoo/html-to-image/issues/532 || https://github.com/bubkoo/html-to-image/issues/508
      ...(this.platform.FIREFOX && { skipFonts: true }),
    }).then((blob) => {
      this.tableWrapper.nativeElement.scroll(currentScrollLeft, currentScrollTop);
      return blob;
    });
  }

  public async exportAsCsv(): Promise<{ fileName?: string; data: any }> {
    let result: DownloadAsDialogResult = {
      recordCount: this.widget.payload.limit,
      fileName: 'Spotlight Export.csv',
      taxonomy: false,
    };

    if (this.showDownloadCsvDialog) {
      result = await this.dialogService
        .showDialog<DownloadAsDialogComponent, DownloadAsDialogOptions>(
          DownloadAsDialogComponent,
          {
            recordCount: this.widget.payload.limit,
            sort: false,
            format: 'csv',
          },
          { width: '600px' },
          false
        )
        .afterClosed()
        .toPromise();
    }

    if (result) {
      const reportData = await this.executeReportForCsv(result);

      const report = this.prepareDataForCsv(reportData);

      if (report) {
        return {
          fileName: result.fileName,
          data: report,
        };
      }
    }
  }

  public formatHeader(value, _axis: 'row' | 'column') {
    return value;
  }

  public formatCellNumber(value) {
    return Utils.addThousandSeparator(value);
  }

  public get query(): string {
    const queryOptions = this.widgetQueryOptions;
    return queryOptions.widgetDataSource
      ? this.filterService.getQueryForDocuments(queryOptions, false, true)
      : '';
  }

  public canShowDataTable(): boolean {
    return false;
  }

  public get hideItemLabel(): string {
    if (this.gethasCustomSet()) {
      return translate('widgets.remove-from-custom-items');
    }

    return translate('widgets.hide-item');
  }

  public get smartSummaryLabel(): string {
    return translate('widgets.smart-summary.label');
  }

  abstract getCellMenuData(row, column, extraData);

  get widgetQueryOptions(): WidgetQueryOptions {
    return this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      this.comparison?.filters[0]
    );
  }

  abstract executeReportForCsv(result: DownloadAsDialogResult);

  abstract prepareDataForCsv(data);

  abstract getCommentsForPoint(point);

  hasPointDocuments(_point): boolean {
    return true;
  }

  onCommentAdded(comment: Comment): void {
    if (comment.target?.pointId) {
      this.commentsByPointId.update((map) => {
        const next = new Map(map);
        if (!next.has(comment.target.pointId)) {
          next.set(comment.target.pointId, []);
        }
        if (comment.titlePinned || comment.summaryPinned) {
          next.get(comment.target.pointId).forEach((comm) => {
            comm.titlePinned = false;
            comm.summaryPinned = false;
          });
        }
        next.set(comment.target.pointId, [...next.get(comment.target.pointId), comment]);
        return next;
      });
    }
  }

  onCommentDeleted(comment: Comment): void {
    if (comment.target?.pointId) {
      this.commentsByPointId.update((map) => {
        const next = new Map(map);
        const remaining = (next.get(comment.target.pointId) ?? []).filter(
          (c) => c.id !== comment.id
        );
        if (remaining.length) {
          next.set(comment.target.pointId, remaining);
        } else {
          next.delete(comment.target.pointId);
        }
        return next;
      });
    }
  }

  onCommentEdited(_comment: Comment): void {}

  openCommentDialog() {
    this.widgetComponent.openCommentDialog(
      this.widget.payload.type === 'focusAreas' ? this.focusAreaItem : this.itemClicked
    );
  }

  showExpansionPanel(panelType: ExpansionPanelType, expansionPanelData: any): void {
    this.menuTriggerElement?.closeMenu();
    super.showExpansionPanel(panelType, expansionPanelData);
  }

  onAddSmartSummaryComment($event) {
    const item = $event.target ?? this.itemClicked ?? this.focusAreaItem;

    this.widgetComponent.openCommentDialog(
      item,
      {
        title: $event.title,
        details: $event.details,
      },
      null,
      'smart-summary',
      $event.smartSummaryId
    );
  }

  onCellCommentIconClicked($event, row, column, extraData) {
    $event.stopImmediatePropagation();

    this.initializePointData(row, column, extraData);

    this.onCommentListOpenClicked(this.itemClicked);
  }

  setAnnotationSelected(commentTarget: CommentTarget) {
    this.selectedCommentPointId = commentTarget?.pointId;
  }

  cellToPoint(row?: string, column?: string): any {
    return { row, column };
  }

  hasCellComments(row?: string, column?: string) {
    return !!this.getCellComments(row, column)?.length;
  }

  hasCellPinnedComment(row, column) {
    return !!this.getCellPinnedComment(row, column);
  }

  getCellComments(row?: string, column?: string): Comment[] | undefined {
    if (this.widget.payload.commentsHidden) {
      return;
    }

    const pointCommentsForDatasource = this.getCommentsForPoint(
      this.cellToPoint(row, column)
    )?.filter((c) => c.payload.widgetDataSourceId === this.widget.dataSourceId);
    if (!pointCommentsForDatasource?.length) {
      return;
    }

    return pointCommentsForDatasource;
  }

  getCellPinnedComment(row?: string, column?: string): Comment {
    if (this.widget.payload.commentsHidden) {
      return;
    }

    return this.getPinnedComment(
      this.getCommentsForPoint(this.cellToPoint(row, column))?.filter(
        (c) => c.payload.widgetDataSourceId === this.widget.dataSourceId
      )
    );
  }

  getPinnedComment(comments: Comment[]) {
    return comments?.find((c) => c.titlePinned || c.summaryPinned);
  }

  pointHasRequiredDocCountForSmartSummaries(point) {
    return (point.documentCount || point.value) >= Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT;
  }

  startAiInsightPointSelectionFlow(handler: MatchmakerHandler) {
    const clickablePoints = this.aiInsightValidatorService.getClickablePoints(handler);
    const pointSelectionRule = this.aiInsightValidatorService.getPointSelectionRule(handler);
    const isApplyComparison = this.aiInsightValidatorService.isApplyComparison(
      handler,
      this.comparison
    );

    this.insightPointSelectionState = {
      active: true,
      pointCount: 0,
      maxPoints: clickablePoints.max,
      pointSelectionRule: pointSelectionRule.rule,
      isApplyComparison,
    };
  }

  onInsightPointSelection(_row, _column, _extraData) {}

  endAiInsightPointSelectionFlow() {
    this.insightPointSelectionState = {
      active: false,
      pointCount: 0,
      maxPoints: 0,
      pointSelectionRule: undefined,
      isApplyComparison: false,
    };
    this.cdr.markForCheck();
  }
}
